/* ==========================================================================
   collection-rail.js — navegação do trilho de coleções
   --------------------------------------------------------------------------
   Vanilla, sem dependência. NÃO usa jQuery.

   Contrato de markup (ver sections/featured-collections.liquid):

     [data-collection-rail]                raiz do bloco (page-width)
       [data-collection-rail-nav]          caixa das setas (hidden por padrão)
         [data-collection-rail-prev]       botão anterior
         [data-collection-rail-next]       botão próximo
       [data-collection-rail-track]        container que rola

   Regras:
   - As setas só aparecem quando existe conteúdo além da área visível
     (scrollWidth > clientWidth). Sem overflow, o bloco fica limpo.
   - Cada seta desabilita ao encostar na respectiva ponta.
   - O estado é recalculado no scroll (via rAF), no resize do trilho e quando
     as imagens terminam de carregar — porque a largura só é real depois disso.
   - Passo de rolagem = uma "página" visível; o scroll-snap do CSS alinha.
   - prefers-reduced-motion desliga a rolagem animada.
   ========================================================================== */

(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-collection-rail]';
  var EDGE_TOLERANCE = 2; // px — subpixel de layout não conta como "chegou ao fim"
  var INSTANCE_KEY = '__collectionRail';

  function prefersReducedMotion() {
    return (
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  function CollectionRail(root) {
    this.root = root;
    this.track = root.querySelector('[data-collection-rail-track]');
    this.nav = root.querySelector('[data-collection-rail-nav]');
    this.prev = root.querySelector('[data-collection-rail-prev]');
    this.next = root.querySelector('[data-collection-rail-next]');

    if (!this.track) return;

    this.ticking = false;
    this.observers = [];
    this.listeners = [];

    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);
    this.update = this.update.bind(this);

    this.bind();
    this.update();

    // Imagens lazy mudam a largura do trilho depois do primeiro paint.
    if (document.readyState !== 'complete') {
      this.on(window, 'load', this.update);
    }
  }

  CollectionRail.prototype.on = function (target, type, handler, options) {
    target.addEventListener(type, handler, options || false);
    this.listeners.push([target, type, handler, options || false]);
  };

  CollectionRail.prototype.bind = function () {
    this.on(this.track, 'scroll', this.onScroll, { passive: true });

    if (this.prev) {
      this.on(
        this.prev,
        'click',
        function () {
          this.scrollByPage(-1);
        }.bind(this)
      );
    }

    if (this.next) {
      this.on(
        this.next,
        'click',
        function () {
          this.scrollByPage(1);
        }.bind(this)
      );
    }

    if (typeof window.ResizeObserver === 'function') {
      var ro = new ResizeObserver(this.onResize);
      ro.observe(this.track);
      var first = this.track.firstElementChild;
      if (first) ro.observe(first);
      this.observers.push(ro);
    } else {
      this.on(window, 'resize', this.onResize);
    }
  };

  CollectionRail.prototype.onScroll = function () {
    if (this.ticking) return;
    this.ticking = true;
    window.requestAnimationFrame(
      function () {
        this.ticking = false;
        this.update();
      }.bind(this)
    );
  };

  CollectionRail.prototype.onResize = function () {
    this.onScroll();
  };

  // scrollLeft é negativo em RTL na maioria dos navegadores modernos.
  CollectionRail.prototype.metrics = function () {
    var track = this.track;
    var max = Math.max(0, track.scrollWidth - track.clientWidth);
    var pos = Math.abs(track.scrollLeft);
    return {
      max: max,
      pos: pos,
      scrollable: max > EDGE_TOLERANCE,
      atStart: pos <= EDGE_TOLERANCE,
      atEnd: pos >= max - EDGE_TOLERANCE
    };
  };

  CollectionRail.prototype.update = function () {
    var m = this.metrics();

    if (this.nav) {
      if (m.scrollable) {
        this.nav.removeAttribute('hidden');
      } else {
        this.nav.setAttribute('hidden', '');
      }
      this.nav.setAttribute('data-scrollable', m.scrollable ? 'true' : 'false');
    }

    if (this.prev) this.prev.disabled = !m.scrollable || m.atStart;
    if (this.next) this.next.disabled = !m.scrollable || m.atEnd;

    this.root.setAttribute('data-rail-scrollable', m.scrollable ? 'true' : 'false');
  };

  CollectionRail.prototype.isRTL = function () {
    return window.getComputedStyle(this.track).direction === 'rtl';
  };

  // Passo = largura visível menos uma folga, para o próximo card não nascer colado
  // na borda. O scroll-snap corrige o resto.
  CollectionRail.prototype.step = function () {
    var track = this.track;
    var first = track.firstElementChild;
    var page = track.clientWidth;

    if (first) {
      var itemWidth = first.getBoundingClientRect().width;
      var styles = window.getComputedStyle(track);
      var gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
      var unit = itemWidth + gap;
      if (unit > 0) {
        var perView = Math.max(1, Math.floor((page + gap) / unit));
        return unit * perView;
      }
    }

    return page;
  };

  CollectionRail.prototype.scrollByPage = function (direction) {
    var delta = this.step() * direction;
    if (this.isRTL()) delta = -delta;

    this.track.scrollBy({
      left: delta,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth'
    });
  };

  CollectionRail.prototype.destroy = function () {
    this.listeners.forEach(function (entry) {
      entry[0].removeEventListener(entry[1], entry[2], entry[3]);
    });
    this.listeners = [];
    this.observers.forEach(function (o) {
      o.disconnect();
    });
    this.observers = [];
    delete this.root[INSTANCE_KEY];
  };

  function init(scope) {
    var container = scope && scope.querySelectorAll ? scope : document;
    var roots = [];

    if (container.matches && container.matches(ROOT_SELECTOR)) roots.push(container);
    Array.prototype.push.apply(roots, container.querySelectorAll(ROOT_SELECTOR));

    roots.forEach(function (root) {
      if (root[INSTANCE_KEY]) {
        root[INSTANCE_KEY].update();
        return;
      }
      root[INSTANCE_KEY] = new CollectionRail(root);
    });
  }

  function destroy(scope) {
    var container = scope && scope.querySelectorAll ? scope : document;
    var roots = container.querySelectorAll ? container.querySelectorAll(ROOT_SELECTOR) : [];
    Array.prototype.forEach.call(roots, function (root) {
      if (root[INSTANCE_KEY]) root[INSTANCE_KEY].destroy();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  // Editor de temas: seções entram e saem do DOM sem recarregar a página.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    destroy(event.target);
  });

  document.addEventListener('shopify:block:select', function (event) {
    var root = event.target.closest ? event.target.closest(ROOT_SELECTOR) : null;
    if (!root || !root[INSTANCE_KEY]) return;
    var track = root[INSTANCE_KEY].track;
    var item = event.target.closest('[data-collection-rail-track] > *') || event.target;
    if (!track.contains(item)) return;
    track.scrollTo({
      left: item.offsetLeft - track.offsetLeft,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth'
    });
  });

  window.CollectionRail = { init: init, destroy: destroy };
})();
